"use client";

import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card_v3";
import { cn } from "lib/utils";
import localization from "services/localization";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

export default function TopPromptsChart({ messages, prompts, theme }) {
  const card_bg =
    theme === "dark" ? "bg-[var(--card-bg)] border-border" : "bg-card border-border";

const promptsMap = useMemo(() => {
  const map = {};
  prompts.forEach((u) => {
    map[u.id] = u;
  });
  return map;
}, [prompts]);

  const data = useMemo(() => {
    const counts = {};
   messages.forEach((item) => {
      item.top_prompts?.forEach((tp) => {
        counts[tp.prompt_id] =
          (counts[tp.prompt_id] || 0) + (tp.count || 0);
      });
    });

    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([promptId, count]) => {
        const prompt = promptsMap[promptId];

        return {
          name: prompt?.title?.It || "Unknown",
          count,
        };
      });
  }, [messages, promptsMap]);

  const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const value = payload?.[0]?.value ?? 0;
  const name = payload?.[0]?.payload?.name ?? "";

  // ✅ total from all bars (data)
  const total = data.reduce((sum, item) => sum + item.count, 0);

  const percentage =
    total > 0 ? ((value / total) * 100).toFixed(1) : 0;

  return (
    <div
      className={cn(
        "rounded-lg border p-3 shadow-lg",
        theme === "dark"
          ? "bg-[#1a2332] border-border text-foreground"
          : "bg-card border-border text-foreground"
      )}
    >
      <p className="text-sm font-medium">{name}</p>

      <div className="mt-1 text-xs text-muted-foreground">
        {localization.usage || "Usage"}:{" "}
        <span className="font-medium text-foreground">
          {value}
        </span>
      </div>

      <div className="text-xs text-muted-foreground">
        {localization.percentage || "Percentage"}:{" "}
        <span className="font-medium text-foreground">
          {percentage}%
        </span>
      </div>
    </div>
  );
};

if (!data.length) {
  return (
    <Card className={cn(card_bg)}>
      <CardContent className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
        {localization.no_data || "No data"}
      </CardContent>
    </Card>
  );
}

  return (
    <Card className={cn(card_bg)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-foreground">
          {localization.top_5_prompts || "Top 5 Prompts Used"}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme === "dark" ? "#1f2937" : "#e5e7eb"}
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: theme === "dark" ? "#9ca3af" : "#6b7280" }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={150}
                tick={{ fontSize: 11, fill: theme === "dark" ? "#9ca3af" : "#6b7280" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={28}>
                {data.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
